import {
  APP_STORE_META,
  ComponentOptionsTypes,
  CustomDocumentFields,
  ELEMENTS_LOOKUP,
  GROUP_LOOKUP,
  IRecipient,
} from '@gohighlevel/ghl-proposals-common'

import { computed } from 'vue'
import { useAppStore } from '@/store/app'
import { useBuilderStore } from '@/store/builder'

export function useElementOption() {
  const store = useBuilderStore()
  const appStore = useAppStore()

  const activeElement = computed(() => store.activeElement)
  const activeElementId = computed(() => store.activeElement?.id)

  function updateOption(
    key: string,
    value: ComponentOptionsTypes | number,
    id?: string,
    type?: string
  ) {
    store.updateElementOption({
      id: id || (activeElement.value?.id as string),
      key,
      value,
      type: type || activeElement.value?.type,
    })
  }

  function updateSectionOption(
    key: string,
    value: ComponentOptionsTypes | number,
    id?: string,
    section?: string
  ) {
    if (section && id) {
      // Use the new method for section-level options
      store.updateSectionElementOption({
        id: id,
        key,
        value,
        type: ELEMENTS_LOOKUP.PAGE_SECTION,
        section: section,
      })
    } else {
      // Fallback to the old method for backward compatibility
      store.updateElementOption({
        id: id || (activeElement.value?.id as string),
        key,
        value,
        type: activeElement.value?.type,
      })
    }
  }

  function updateStyles(key: string, value: any, id?: string, type?: string) {
    store.updateElementStyle({
      id: id || (activeElement.value?.id as string),
      key,
      value,
      type: type || activeElement.value?.type,
    })
  }

  function updateSectionStyles(
    id: string,
    key: string,
    value: any,
    section?: string
  ) {
    store.updateSectionElementStyle({
      id: id,
      key,
      value,
      type: ELEMENTS_LOOKUP.PAGE_SECTION,
      section: section || '',
    })
  }

  function updateDocumentOption(
    key: keyof CustomDocumentFields,
    value: CustomDocumentFields[keyof CustomDocumentFields]
  ) {
    if (key === APP_STORE_META.RECIPIENTS) {
      //In case an element is set for sender, first assign it to the current loggedin user then assign the rest of the recipients
      store.autoAssignSenderIfNotPresentInRecipientList()
      appStore.updateDocumentOption(
        key,
        (value as IRecipient[])?.map((recipient, index) => {
          return {
            ...recipient,
            signingOrder: index + 1,
          }
        })
      )
      store.autoAssignIfRecipientIdIsNotPresentInRecipientList()
    } else {
      appStore.updateDocumentOption(key, value)
    }
  }

  function updateGroupOption(
    key: string,
    value: ComponentOptionsTypes | number,
    id: string
  ) {
    store.updateGroupOption({
      id,
      key,
      value,
    })
  }

  function updateGroupStyle(key: string, value: any | number, id: string) {
    store.updateGroupStyle({
      id,
      key,
      value,
      type: GROUP_LOOKUP.GROUP_FIELDS,
    })
  }

  return {
    updateOption,
    updateSectionOption,
    activeElement,
    activeElementId,
    updateStyles,
    updateSectionStyles,
    updateDocumentOption,
    updateGroupOption,
    updateGroupStyle,
  }
}
